import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';
import type { ToastType } from '../../hooks/useToast';

interface ToastProps {
  message: string;
  type?: ToastType;
  onClose: () => void;
  duration?: number;
}

export default function Toast({ 
  message, 
  type = 'success', 
  onClose, 
  duration = 5000 
}: ToastProps) {
  const { t } = useTranslation('common');

  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-emerald-400" />,
    error: <AlertCircle className="w-5 h-5 text-red-400" />,
    info: <Info className="w-5 h-5 text-blue-400" />
  };

  const bgColors = {
    success: 'bg-emerald-50 border-emerald-100',
    error: 'bg-red-50 border-red-100',
    info: 'bg-blue-50 border-blue-100'
  };

  const textColors = {
    success: 'text-emerald-800',
    error: 'text-red-800',
    info: 'text-blue-800'
  };

  return (
    <div 
      role="alert"
      aria-live="polite"
      className={`
        fixed bottom-4 right-4 
        ${bgColors[type]}
        border rounded-lg
        px-4 py-3
        shadow-lg
        flex items-center 
        animate-fade-in
        z-50
        max-w-md
      `}
    >
      <div className="flex items-center space-x-3 flex-1">
        {icons[type]}
        <span className={`font-medium text-sm ${textColors[type]}`}>
          {message}
        </span>
      </div>
      <button 
        onClick={onClose}
        className={`ml-4 ${textColors[type]} opacity-70 hover:opacity-100 transition-opacity`}
        aria-label={t('actions.close')}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}